import React, { useState, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

function randomOffset() {
  // Simulate small movement (about 10-30 meters)
  return (Math.random() - 0.5) * 0.0005;
}

export default function LocationTest() {
  const [imei, setImei] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const intervalRef = useRef(null);
  const [currentLat, setCurrentLat] = useState('');
  const [currentLon, setCurrentLon] = useState('');

  const handleNow = () => {
    // Not needed for auto mode, but keep for manual
    setResult(null);
  };

  const sendLocation = async (latToSend, lonToSend) => {
    setLoading(true);
    setResult(null);
    try {
      const timestamp = new Date().toISOString();
      const res = await fetch(`${API_URL}/api/admin/get-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({ imei, lat: latToSend, lon: lonToSend, timestamp }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: 'Location sent successfully!', data });
        setLastSent({ lat: latToSend, lon: lonToSend, timestamp });
      } else {
        setResult({ type: 'error', message: data.message || 'Error sending location', data });
      }
    } catch (err) {
      setResult({ type: 'error', message: err.message });
    }
    setLoading(false);
  };

  const handleStart = () => {
    if (!imei || !lat || !lon) {
      setResult({ type: 'error', message: 'Please enter IMEI, latitude, and longitude to start.' });
      return;
    }
    setSimulating(true);
    setCurrentLat(parseFloat(lat));
    setCurrentLon(parseFloat(lon));
    sendLocation(parseFloat(lat), parseFloat(lon)); // Send initial point
    intervalRef.current = setInterval(() => {
      setCurrentLat(prevLat => {
        const newLat = parseFloat(prevLat) + randomOffset();
        setCurrentLon(prevLon => {
          const newLon = parseFloat(prevLon) + randomOffset();
          sendLocation(newLat, newLon);
          return newLon;
        });
        return newLat;
      });
    }, 2000);
  };

  const handleStop = () => {
    setSimulating(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Send one final location with old timestamp to make device inactive
    if (imei && currentLat && currentLon) {
      const oldTimestamp = new Date(Date.now() - 10 * 60 * 1000).toISOString(); // 10 minutes ago
      fetch(`${API_URL}/api/admin/get-location`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        },
        body: JSON.stringify({ 
          imei, 
          lat: currentLat, 
          lon: currentLon, 
          timestamp: oldTimestamp 
        }),
      }).then(() => {
        setResult({ type: 'success', message: 'Simulation stopped. Device marked as inactive.' });
      }).catch(err => {
        setResult({ type: 'error', message: 'Error stopping simulation: ' + err.message });
      });
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '40px auto', padding: 24, border: '1px solid #ccc', borderRadius: 8, background: '#fafbfc' }}>
      <h2>Test Device Location & Status (Auto Path)</h2>
      <form onSubmit={e => e.preventDefault()}>
        <div style={{ marginBottom: 12 }}>
          <label>Device IMEI: <input value={imei} onChange={e => setImei(e.target.value)} required disabled={simulating} style={{ width: '100%' }} /></label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Start Latitude: <input value={lat} onChange={e => setLat(e.target.value)} required disabled={simulating} style={{ width: '100%' }} /></label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label>Start Longitude: <input value={lon} onChange={e => setLon(e.target.value)} required disabled={simulating} style={{ width: '100%' }} /></label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <button type="button" onClick={handleStart} disabled={simulating || loading} style={{ padding: '8px 16px', background: '#388e3c', color: '#fff', border: 'none', borderRadius: 4, marginRight: 8 }}>Start</button>
          <button type="button" onClick={handleStop} disabled={!simulating} style={{ padding: '8px 16px', background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 4 }}>Stop</button>
        </div>
      </form>
      {simulating && <div style={{ color: '#388e3c', marginBottom: 8 }}>Simulating path... Sending a new location every 2 seconds.</div>}
      {lastSent && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#1976d2' }}>
          Last sent: lat {lastSent.lat}, lon {lastSent.lon}, time {lastSent.timestamp}
        </div>
      )}
      {result && (
        <div style={{ marginTop: 20, color: result.type === 'success' ? 'green' : 'red' }}>
          <b>{result.message}</b>
          {result.data && <pre style={{ background: '#eee', padding: 8, borderRadius: 4, marginTop: 8 }}>{JSON.stringify(result.data, null, 2)}</pre>}
        </div>
      )}
      <div style={{ marginTop: 32, fontSize: 13, color: '#888' }}>
        <b>How to use:</b><br />
        - Enter a device IMEI and starting latitude/longitude.<br />
        - Click <b>Start</b> to begin sending random path points (device will stay active).<br />
        - Click <b>Stop</b> to end the simulation.<br />
        - Watch your map update in real time.<br />
        <br />
        <b>Note:</b> Remove this page after testing!
      </div>
    </div>
  );
}